/**
 * Mikrofon catalog sections on /produkter?category=Mikrofon.
 * Classification is use-case via slug/id — not brand series (BLX/SLX) or price tier.
 */

export type MikrofonTierId =
  | 'tradlosa'
  | 'sang_instrument'
  | 'headset_presentation'
  | 'tillbehor_ovrigt';

/** Primary use-case sections (hero weight). */
export const MIKROFON_PRIMARY_TIER_ORDER: readonly Exclude<
  MikrofonTierId,
  'tillbehor_ovrigt'
>[] = ['tradlosa', 'sang_instrument', 'headset_presentation'] as const;

export const MIKROFON_TIER_ORDER: readonly MikrofonTierId[] = [
  ...MIKROFON_PRIMARY_TIER_ORDER,
  'tillbehor_ovrigt',
] as const;

export const MIKROFON_TIER_STICKY_NAV = false;

export const MIKROFON_TIER_COPY: Readonly<
  Record<MikrofonTierId, { title: string; blurb: string }>
> = {
  tradlosa: {
    title: 'Trådlösa mikrofoner',
    blurb:
      'Smidiga mikrofonlösningar för tal, presentationer, bröllop, konferenser och event.',
  },
  sang_instrument: {
    title: 'Sång & instrument',
    blurb:
      'Professionella mikrofoner för sång, instrument, DJ och liveframträdanden.',
  },
  headset_presentation: {
    title: 'Headset & presentation',
    blurb:
      'Diskreta och professionella mikrofonlösningar för föreläsare, konferenser, presentationer och scen.',
  },
  tillbehor_ovrigt: {
    title: 'Tillbehör & övrigt',
    blurb: 'Signal och tillbehör som kompletterar mikrofonuppsättningen.',
  },
};

/** Explicit live mapping (verified 2026-09-17). One section per product. */
export const MIKROFON_TIER_BY_SLUG: Readonly<Record<string, MikrofonTierId>> = {
  // Trådlösa (handhållna / mottagarsystem) — use-case, not BLX series
  'shure-blx288-sm58-tradlost-mikrofonsystem-med-tva-handmikrofoner': 'tradlosa',
  'tradlos-mikrofon-duo-2-x-handhallen': 'tradlosa',
  'tradlost-mikrofonset': 'tradlosa',
  'mikrofonset-quad': 'tradlosa', // verified wireless: 2× 2-ch receivers, “utan kablar”
  // Sång & instrument
  'shure-sm58-mikrofon-med-stativ-och-xlr-kabel': 'sang_instrument',
  'shure-sm57-instrumentmikrofon-ink-mikrofonstativ': 'sang_instrument',
  '4x-shure-sm58-mikrofonpaket-med-stativ-kablar': 'sang_instrument',
  'shure-dmk57-52-mikrofonkit-for-trummor-och-instrument': 'sang_instrument',
  'line-audio-cm4-smamembrans-kondensatormikrofon': 'sang_instrument',
  // Headset & presentation
  'headset-slipsmikrofon-duo-handsfree': 'headset_presentation',
  'shure-blx14r-mx53-tradlost-headsetsystem': 'headset_presentation',
  // DI-box / non-mic — discrete fallback (not a primary use-case section)
  'radial-prod2-professionell-stereo-di-box': 'tillbehor_ovrigt',
};

export const MIKROFON_TIER_BY_ID: Readonly<Record<string, MikrofonTierId>> = {
  '8ef582c3-45c2-4818-8de2-0d698ac2c512': 'tradlosa',
  '742713e7-d206-4172-a439-6b17a5131ee4': 'tradlosa',
  'ec1ece3e-f38b-44fe-8de6-55fb9697dc4c': 'tradlosa',
  '2b8885be-6f8d-495b-9650-9336a41e38c2': 'tradlosa',
  '07c4ef5b-b9bb-4830-82f0-9aa708a66e68': 'sang_instrument',
  'f840bf80-0821-4f37-9690-de75051a38b0': 'sang_instrument',
  'f0c0d0b2-e984-48c6-a2e1-717685d663d9': 'sang_instrument',
  '46d776f3-21f3-4388-8b37-a8d0f68e850c': 'sang_instrument',
  'ab72e87b-1f9d-4400-8721-25be1a87f079': 'sang_instrument',
  '2b9710a1-6555-4a9a-ae05-188ffac551c2': 'headset_presentation',
  'b61a4323-72d8-42a1-b685-014c2b8b41b9': 'headset_presentation',
  '019e06bd-c12b-471a-8c53-27ef2254702b': 'tillbehor_ovrigt',
};

export type MikrofonTierProductRef = {
  id?: string;
  slug?: string | null;
  name?: string;
};

/**
 * Resolve section. Explicit slug, then id. Name is ignored for mapped products.
 * Unknown products fall back to tradlosa (defensive).
 */
export function resolveMikrofonTier(
  product: MikrofonTierProductRef
): MikrofonTierId {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  if (slug && MIKROFON_TIER_BY_SLUG[slug]) {
    return MIKROFON_TIER_BY_SLUG[slug];
  }
  const id = String(product.id ?? '').trim();
  if (id && MIKROFON_TIER_BY_ID[id]) {
    return MIKROFON_TIER_BY_ID[id];
  }
  return 'tradlosa';
}

export function isMikrofonPrimaryTier(tier: MikrofonTierId): boolean {
  return tier !== 'tillbehor_ovrigt';
}

export type MikrofonTierGroup<T extends MikrofonTierProductRef> = {
  tier: MikrofonTierId;
  title: string;
  blurb: string;
  products: T[];
  primary: boolean;
};

export function groupMikrofonProducts<T extends MikrofonTierProductRef>(
  products: readonly T[]
): MikrofonTierGroup<T>[] {
  const buckets: Record<MikrofonTierId, T[]> = {
    tradlosa: [],
    sang_instrument: [],
    headset_presentation: [],
    tillbehor_ovrigt: [],
  };
  for (const product of products) {
    buckets[resolveMikrofonTier(product)].push(product);
  }
  return MIKROFON_TIER_ORDER.map((tier) => ({
    tier,
    title: MIKROFON_TIER_COPY[tier].title,
    blurb: MIKROFON_TIER_COPY[tier].blurb,
    products: buckets[tier],
    primary: isMikrofonPrimaryTier(tier),
  })).filter((g) => g.products.length > 0);
}
